import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, CircleDollarSign } from "lucide-react";
import { adminAction } from "@/lib/adminApi";
import {
  AdminPageHeader,
  AdminPanel,
  AdminSection,
  AdminTableSkeleton,
  AdminEmptyState,
  AdminHeroMetric,
  AdminHeroAsideStat,
  AdminStatRibbon,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "./AdminUI";

function reasonTone(r?: string): "warning" | "info" | "danger" | "neutral" {
  switch (r) {
    case "too_expensive": return "warning";
    case "missing_features": return "info";
    case "switched_competitor": return "danger";
    default: return "neutral";
  }
}

export function AdminChurnTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "churn"],
    queryFn: () => adminAction("churn"),
  });

  // Server returns { feedback, rate: { cancelled_this_month, base, pct } }.
  const churnData = (data?.feedback ?? []) as any[];
  const rate = data?.rate ?? { cancelled_this_month: 0, base: 0, pct: 0 };
  const cancelledThisMonth = rate.cancelled_this_month;
  const churnRate = rate.base > 0 ? `${rate.pct.toFixed(1)}%` : "0%";

  const { data: atRiskData } = useQuery({
    queryKey: ["admin", "at_risk_orgs"],
    queryFn: () => adminAction("organizations"),
  });

  const atRiskCount = useMemo(() => {
    const orgs = atRiskData || [];
    const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
    return orgs.filter((o: any) => {
      const last = o.last_active || o.last_activity_at || o.updated_at;
      if (!last) return false;
      return new Date(last).getTime() < cutoff && o.status?.toLowerCase() === "active";
    }).length;
  }, [atRiskData]);

  const { data: dunning } = useQuery({
    queryKey: ["admin", "dunning_summary"],
    queryFn: () => adminAction("dunning_summary"),
  });

  return (
    <div className="space-y-8">
      <AdminPageHeader
        eyebrow="Revenue"
        title="Churn"
        description="Cancellations, at-risk customers, and dunning recovery in one view."
      />

      <AdminHeroMetric
        eyebrow="Churn rate · This month"
        value={churnRate}
        caption="Share of last month's paying customers that cancelled in the current window. Healthy SaaS targets sit below 5%."
        tone="danger"
        aside={
          <>
            <AdminHeroAsideStat label="Cancelled · MTD" value={cancelledThisMonth} />
            <AdminHeroAsideStat label="At-risk · 14d quiet" value={atRiskCount} />
          </>
        }
      />

      <AdminSection title="Failed payment recovery" description="Stripe dunning attempts across the last 200 events.">
        <AdminStatRibbon
          items={[
            { label: "Active dunning", value: dunning?.activeCount ?? 0 },
            { label: "Recovered", value: dunning?.recoveredCount ?? 0 },
            { label: "Lost to cancel", value: dunning?.cancelledCount ?? 0 },
            { label: "Recovery rate", value: `${dunning?.recoveryRate ?? 0}%` },
          ]}
        />
        {dunning?.active?.length ? (
          <AdminPanel>
            <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
                <tr>
                  <th className={adminTableHeadCellClass}>Email</th>
                  <th className={adminTableHeadCellClass}>Amount</th>
                  <th className={adminTableHeadCellClass}>Plan</th>
                  <th className={adminTableHeadCellClass}>First failed</th>
                  <th className={adminTableHeadCellClass}>Stage</th>
                </tr>
              </thead>
              <tbody>
                {dunning.active.map((d: any, i: number) => {
                  const stage = d.day7_sent_at ? "Day 7 (final)" : d.day3_sent_at ? "Day 3" : "Day 0";
                  const tone = d.day7_sent_at ? "danger" : d.day3_sent_at ? "warning" : "neutral";
                  return (
                    <tr key={d.id} className={adminTableRowClass} style={{ borderTop: i === 0 ? "none" : "1px solid rgba(58,53,48,0.06)" }}>
                      <td className={adminTableCellClass + " text-foreground"}>{d.recipient_email}</td>
                      <td className={adminTableCellClass + " text-foreground tabular-nums"}>{d.amount || "—"}</td>
                      <td className={adminTableCellClass + " text-muted-foreground capitalize"}>{d.plan || "—"}</td>
                      <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                        {new Date(d.first_failed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      <td className={adminTableCellClass}><AdminPill tone={tone}>{stage}</AdminPill></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </AdminPanel>
        ) : (
          <AdminEmptyState icon={CircleDollarSign} title="No failed payments" description="Everyone is paid up — nothing to recover." />
        )}
      </AdminSection>

      <AdminSection title="Churn feedback" description="Reasons given by users who cancelled.">
        {isLoading ? (
          <AdminTableSkeleton rows={5} columns={4} />
        ) : !churnData.length ? (
          <AdminEmptyState icon={Check} title="No churn feedback yet" description="Feedback shows up here when a customer cancels." />
        ) : (
          <AdminPanel>
            <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
                <tr>
                  <th className={adminTableHeadCellClass}>Email</th>
                  <th className={adminTableHeadCellClass}>Reason</th>
                  <th className={adminTableHeadCellClass}>Date</th>
                  <th className={adminTableHeadCellClass}>Follow-up</th>
                </tr>
              </thead>
              <tbody>
                {churnData.map((c: any, i: number) => (
                  <tr key={c.id} className={adminTableRowClass} style={{ borderTop: i === 0 ? "none" : "1px solid rgba(58,53,48,0.06)" }}>
                    <td className={adminTableCellClass + " text-foreground"}>{c.user_email}</td>
                    <td className={adminTableCellClass}>
                      {c.reason ? (
                        <AdminPill tone={reasonTone(c.reason)}>{c.reason.replace(/_/g, " ")}</AdminPill>
                      ) : (
                        <span className="text-muted-foreground text-[13px]">Pending</span>
                      )}
                    </td>
                    <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                      {new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className={adminTableCellClass}>
                      {c.followup_email_sent_at ? <Check className="w-4 h-4 text-[#16a34a]" /> : <span className="text-[13px] text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminPanel>
        )}
      </AdminSection>
    </div>
  );
}
